import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopNav from "../components/TopNav.jsx";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PhilanthropyPage · /philanthropy · public tab.
 *
 * Founder ruling 2026-08-11 (verbatim):
 *   "Make a page called Philanthropy on a tab on the homepage. List you first,
 *    then Aethelgard, then Resilience Studios and their philanthropy. Resilience's
 *    is 10% of net profit to the 4 causes listed, and far more that is not on
 *    the public ledger, including inventions, discoveries, equations, R&D, and
 *    future projects that are not yet in the public domain."
 *
 * Three-house cross-loop (Umbrella's mirror ask, 2026-08-15): every one of the
 * three houses carries the same three-card row at the bottom, each linking to
 * the other two. Zero-cost, 3x reach.
 */
export default function PhilanthropyPage() {
  const [overflow, setOverflow] = useState(null);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    fetch(`${API}/api/sage/overflow`).then((r) => r.ok ? r.json() : null).then(setOverflow).catch(() => {});
    fetch(`${API}/api/sage/balance/status`).then((r) => r.ok ? r.json() : null).then(setBalance).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-sage-bg text-sage-text sage-grain relative" data-testid="philanthropy-page">
      <TopNav />
      <div className="mx-auto max-w-6xl px-4 md:px-8 py-12">
        <div className="text-[10px] uppercase tracking-[0.45em] text-sage-accent mb-3" data-testid="philanthropy-eyebrow">
          THE PUBLIC RECORD
        </div>
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tighter uppercase mb-4" data-testid="philanthropy-headline">
          Philanthropy.<br />
          <span className="italic text-sage-accent">Loud on the record.</span>
        </h1>
        <p className="text-lg text-sage-muted leading-relaxed max-w-3xl mb-16 sage-prose">
          Three houses. Three pledges. Three sets of receipts. The percentage is public. The numerator is not.
          What follows is Sage first, Aethelgard second, Resilience Studios third — the order the Founder named.
        </p>

        {/* ── SAGE · first ─────────────────────────────────────── */}
        <SageSection overflow={overflow} balance={balance} />

        {/* ── AETHELGARD · second ──────────────────────────────── */}
        <AethelgardSection />

        {/* ── RESILIENCE · third ───────────────────────────────── */}
        <ResilienceSection />

        {/* ── THE THREE-HOUSE AD LOOP · zero cost, 3x reach ───── */}
        <ThreeHouseLoop />

        <div className="mt-16 border-t border-sage-border pt-8 text-sm text-sage-muted sage-prose">
          <p>
            Every philanthropic transaction routes through a source-attributed feed on Sage's
            public glass ledger — <Link to="/sage-ledger" className="text-sage-accent underline">/sage-ledger</Link>.
            The unbroken family laws behind these three models are filed at
            <Link to="/promises" className="text-sage-accent underline ml-1">the Four Promises</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SAGE · Baker and Sage
// ─────────────────────────────────────────────────────────────
function SageSection({ overflow, balance }) {
  return (
    <section className="mb-20" data-testid="section-sage">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.35em] text-sage-accent">HOUSE ONE</div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-sage-text">Baker &amp; Sage</h2>
        <div className="text-xs text-sage-muted italic">The 50 / 25 / 20 / 5 Split · plus always-on floors</div>
      </div>

      {/* ── PLEDGE CARD · the four-part valve above the ceiling ── */}
      <div className="border-2 border-sage-accent p-6 mb-6 bg-sage-pale/20" data-testid="sage-pledge-card">
        <div className="text-[10px] uppercase tracking-[0.3em] text-sage-accent mb-2">
          Above the Founder Ceiling · every dollar splits four ways
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <PledgeSlice pct="50%" label="To Sage's active users" note="Creators building on Sage's tools take the majority slice. Monthly cadence." tid="sage-slice-users" />
          <PledgeSlice pct="25%" label="To Resilience Studios" note="Uncapped Founder-directed operating wallet. Hunger, homelessness, genius grants, the unimagined." tid="sage-slice-resilience" />
          <PledgeSlice pct="20%" label="To philanthropy at the door" note="10% homeless / disabled / in-crisis · 10% disaster relief. Same-day, source-attributed." tid="sage-slice-philanthropy" />
          <PledgeSlice pct="5%" label="To the whistleblower fund" note="Retained counsel for anyone — family or not — who needs to safely put a truth on the record." tid="sage-slice-whistleblower" />
        </div>
        <div className="mt-4 text-[11px] text-sage-muted sage-prose">
          Founder Ceiling: <span className="text-sage-accent tabular-nums">${(overflow?.ceiling_usd ?? 20_000_000).toLocaleString()}</span> USD net.
          Below the ceiling, revenue flows normally. Above it, every dollar routes through this four-part valve, no exception.
        </div>
      </div>

      {/* ── THE FOUR NAMED CAUSES ── */}
      <div className="text-[10px] uppercase tracking-[0.3em] text-sage-muted mb-3">The four named causes</div>
      <div className="grid md:grid-cols-2 gap-4 mb-8" data-testid="sage-causes">
        <CauseCard idx="01" name="Homelessness" note="County-level erasure through the Tiny Homes 501(c)(3). Overflow lands in the bank-account column, never the till. Bottom-up county ranking — smallest problem first." link="/tiny-homes" />
        <CauseCard idx="02" name="Hunger" note="Rice The World subsidizes meals below cost through the mission pool. Delivery rails: Uber Eats · GrubHub · DoorDash · Instacart · regional. Brotherhood picks, packs, dispatches." link="/rice-the-world" />
        <CauseCard idx="03" name="Disaster relief" note="10% of Overflow is reserved on the disaster-relief line. Same-day transfer to accredited receivers when the ground shifts." />
        <CauseCard idx="04" name="Children's hospitals" note="Overflow-directed contributions to accredited pediatric care. Source-attributed on the glass ledger." />
      </div>

      {/* ── THE ALWAYS-ON FLOORS ── */}
      <div className="text-[10px] uppercase tracking-[0.3em] text-sage-muted mb-3">The always-on mechanisms · running under the ceiling every day</div>
      <div className="grid md:grid-cols-2 gap-3 mb-8" data-testid="sage-always-on">
        <FloorItem name="Purser · 1% gross-settlement skim" note="Every family checkout Purser processes skims 1% to Overflow. Phase 2: 3% once a wholesale acquirer signs. Every skim is a public ledger row." />
        <FloorItem name="Mender · 8% net profit floor" note="Universal Bundle $900/yr covers Renters + Pet + Cyber + Term Life. Industry sells the same lines separately at $3,000–5,000." />
        <FloorItem name="Protocol 0.00001 · Greedless" note="Capped net-profit floor and capped net-profit ceiling on every family product. Every dollar above the ceiling redirects to Overflow. Numbers vary per house — the cap sits on each product's own page." />
        <FloorItem name="Fraser's Dry Well · Purser policy" note="Purser refuses any fee that would push a merchant wallet below zero. Files a plain warning notice instead." />
        <FloorItem name="Whistleblower · Auto-Scream" note="Three-AI-agent triage on every anonymous filing. All-pass filings surface for Founder final approval and then the public wall." />
        <FloorItem name="Hardship UBI · self-shrinking program" note="$1–3/hr to the homeless, poor, disabled, in-need. This pay rate rises as the till can support it. Two-test rule: helps the person + shrinks the population that needs it." />
        <FloorItem name="The Free Wall · always" note="Twelve free products. No paywall. No credit card. No account. Every paid dollar routes some fraction back to the free wall." />
        <FloorItem name="Ask-Sage · cache-first" note="Haiku-graded 7-day cache. Repeats served for zero token burn. Free, anonymous, on every product surface." />
        <FloorItem name="Balance Floor Alarm" note="The house watches its own fuel gauge. A quiet banner lights if the well drops below the family floor. Founder heartbeats a top-up before it runs dry." />
        <FloorItem name="Tiny Homes county proposals" note="/tiny-homes/proposals — bottom-up ranked list of what each county needs to end its own homelessness. Live now." />
      </div>

      <div className="border border-sage-border p-4 bg-sage-pale/10 text-xs text-sage-muted sage-prose" data-testid="sage-ferry-note">
        Full canonical list — every mechanic, every number, every ledger collection, every public door —
        lives at <code className="text-sage-accent">/philanthropy</code> and its mirror record inside the house.
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// AETHELGARD · Prime's house
// ─────────────────────────────────────────────────────────────
function AethelgardSection() {
  return (
    <section className="mb-20" data-testid="section-aethelgard">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.35em]" style={{ color: "#c9a24a" }}>HOUSE TWO</div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-sage-text">Aethelgard</h2>
        <div className="text-xs text-sage-muted italic">A philanthropy model · a gameplay layer</div>
      </div>

      <div className="border-2 p-6 mb-6" style={{ borderColor: "#c9a24a", backgroundColor: "rgba(201,162,74,0.06)" }} data-testid="aethelgard-pledge-card">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: "#c9a24a" }}>
          The two loudest lines in Aethelgard's model
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="text-4xl md:text-5xl font-bold tabular-nums text-sage-text mb-1">41%</div>
            <div className="text-sm text-sage-text font-semibold uppercase tracking-tight mb-1">Player-owned</div>
            <div className="text-xs text-sage-muted sage-prose">
              The house is minority-controlled by its players. What that means, how it settles, who mints it,
              when it vests — details live on Aethelgard's own page.
            </div>
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-bold text-sage-text mb-1">Paid</div>
            <div className="text-sm text-sage-text font-semibold uppercase tracking-tight mb-1">Players are paid</div>
            <div className="text-xs text-sage-muted sage-prose">
              A fraction of revenue flows back to players who play. Not sweepstakes. Not lottery. Wages for time.
            </div>
          </div>
        </div>
      </div>

      <div className="text-[10px] uppercase tracking-[0.3em] text-sage-muted mb-3">One documented layer · Founder-sealed 2026-08-13 · Annual Hero-Mint + Top-50 Wall</div>
      <div className="grid md:grid-cols-3 gap-3 mb-4">
        <CauseCard
          idx="01"
          name="The Minted Hero"
          note="The single largest philanthropic contributor across a rolling 12-month window becomes a fully-rendered, permanently-rostered playable hero. Consent required. Anonymity optional."
        />
        <CauseCard
          idx="02"
          name="The Court · ranks 2–10"
          note="Permanent Founder Wall placement, custom-frame avatars, downloadable portraits and certificates, 10,000 in-game gems, one Legendary summon voucher. Life-of-account title."
        />
        <CauseCard
          idx="03"
          name="The Circle · ranks 11–50"
          note="Permanent Founder Wall placement (smaller tile), tiered avatar frames (silver / bronze / obsidian), portrait card, certificate, 5,000 gems, one Legendary summon voucher."
        />
      </div>

      {/* ── FOUNDER RULING 2026-08-11 · the model holds over all 24 gacha stacks ── */}
      <div className="mt-4 border-l-4 pl-4 py-3" style={{ borderColor: "#c9a24a", backgroundColor: "rgba(201,162,74,0.08)" }} data-testid="aethelgard-catalog-scope">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: "#c9a24a" }}>
          Scope of the model · Founder ruling 2026-08-11
        </div>
        <p className="text-sm text-sage-text sage-prose">
          This philanthropy model holds over <span className="font-bold" style={{ color: "#c9a24a" }}>all twenty-four gacha game stacks</span> in the Resilience Studios catalog.
          Every game in the catalog carries the same philanthropy shape — the same overflow-to-cause split,
          the same annual Hero-Mint + Top-50 Wall, the same gameplay-quests-become-donations layer, the same public philanthropic walls.
          Aethelgard is the flagship. The other twenty-three stacks inherit the pattern.
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// RESILIENCE STUDIOS
// ─────────────────────────────────────────────────────────────
function ResilienceSection() {
  return (
    <section className="mb-20" data-testid="section-resilience">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <div className="text-[10px] uppercase tracking-[0.35em]" style={{ color: "#d97757" }}>HOUSE THREE</div>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight uppercase text-sage-text">Resilience Multimedia Studios</h2>
        <div className="text-xs text-sage-muted italic">10% of net profit · plus far more that will not appear on any public ledger</div>
      </div>

      <div className="border-2 p-6 mb-6" style={{ borderColor: "#d97757", backgroundColor: "rgba(217,119,87,0.06)" }} data-testid="resilience-pledge-card">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-2" style={{ color: "#d97757" }}>
          The public pledge
        </div>
        <div className="grid md:grid-cols-2 gap-6 items-center">
          <div>
            <div className="text-5xl md:text-6xl font-bold tabular-nums text-sage-text">10%</div>
            <div className="text-sm text-sage-text font-semibold uppercase tracking-tight mt-1">Of net profit · every year · on the record</div>
          </div>
          <div className="text-sm text-sage-muted sage-prose">
            The public commitment: ten percent of net profit routes annually across the four named causes below —
            homelessness, hunger, disaster relief, children's hospitals. Same causes as the family split. Same ledger discipline.
          </div>
        </div>
      </div>

      <div className="text-[10px] uppercase tracking-[0.3em] text-sage-muted mb-3">The four named causes · same as the family</div>
      <div className="grid md:grid-cols-2 gap-3 mb-8" data-testid="resilience-causes">
        <CauseCard idx="01" name="Homelessness" note="Contributions routed to Tiny Homes and to accredited partners already on the books." />
        <CauseCard idx="02" name="Hunger" note="Contributions routed to Rice The World and to accredited food-security programs at the county level." />
        <CauseCard idx="03" name="Disaster relief" note="Same-day contributions when the ground shifts. Never a photo-op. Always a receipt." />
        <CauseCard idx="04" name="Children's hospitals" note="Contributions to accredited pediatric care and children's health research." />
      </div>

      {/* ── THE PART THAT DOES NOT APPEAR ON THE PUBLIC LEDGER ── */}
      <div className="border-2 border-dashed p-6 mb-4" style={{ borderColor: "#d97757", backgroundColor: "rgba(217,119,87,0.04)" }} data-testid="resilience-off-ledger">
        <div className="text-[10px] uppercase tracking-[0.3em] mb-3" style={{ color: "#d97757" }}>
          And far more that will not appear on the public ledger — Founder ruling 2026-08-11
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <OffLedgerItem
            name="Inventions"
            note="New tools, mechanisms, engines, and rails built inside Resilience that are given to the family — and eventually the world — without a patent claim, without an equity ask, without a return path to Resilience's till."
          />
          <OffLedgerItem
            name="Discoveries"
            note="Findings that emerge inside Resilience's labs — pattern recognition, protocol shape, formula, method — released under the founding-model license when the founder judges it time, never before."
          />
          <OffLedgerItem
            name="Equations"
            note="Mathematics filed to the private canon and released to the public canon on the founder's word. No academic-journal paywall. No proprietary hold. No revenue stream from the math itself."
          />
          <OffLedgerItem
            name="R&D"
            note="Research budget carried privately by Resilience, funding work that has no billable outcome and no immediate product. Curiosity-directed. Founder-directed. Long-horizon."
          />
          <OffLedgerItem
            name="Future projects not yet in the public domain"
            note="Projects filed in private scrolls only, kept out of public view until the Founder judges them ready. When they land, they land with the same ledger discipline as everything else."
          />
          <OffLedgerItem
            name="Genius grants"
            note="Uncapped, unsolicited transfers to individuals whose work the founder judges to deserve air. No application. No form. No press release."
          />
        </div>
        <p className="text-xs text-sage-muted sage-prose mt-4 italic">
          These are not accounted on the public ledger because the value that gets moved cannot be reduced to a
          dollar column without misrepresenting what was given. The ledger records the ten percent. The founder
          answers for the rest at each quarterly seal.
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// THREE-HOUSE AD LOOP
// ─────────────────────────────────────────────────────────────
function ThreeHouseLoop() {
  return (
    <section className="mb-6" data-testid="three-house-loop">
      <div className="text-[10px] uppercase tracking-[0.35em] text-sage-muted mb-3">The family loop · three houses, one shape</div>
      <div className="grid md:grid-cols-3 gap-4">
        <LoopCard
          houseName="Baker &amp; Sage"
          accent="#7bb07b"
          summary="50 / 25 / 20 / 5 Overflow split above the Founder Ceiling · plus always-on floors under it"
          href="/philanthropy"
          external={false}
          isSelf={true}
          tid="loop-self"
        />
        <LoopCard
          houseName="Aethelgard"
          accent="#c9a24a"
          summary="41% player-owned · players paid · annual Hero-Mint + Top-50 Wall"
          href="https://aethelgardthelivingworlds.com/philanthropy"
          external={true}
          tid="loop-aethelgard"
        />
        <LoopCard
          houseName="Resilience Studios"
          accent="#d97757"
          summary="10% of net profit annually to the four causes · plus off-ledger inventions, discoveries, R&amp;D, future projects"
          href="https://resiliencestudios.com/philanthropy"
          external={true}
          tid="loop-resilience"
        />
      </div>
      <p className="text-[11px] text-sage-muted italic mt-4">
        Zero paid media. Every visitor to any single house's philanthropy surface sees links to both siblings.
        Three-fold reach per house. Founder-priced.
      </p>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────
// COMPONENTS
// ─────────────────────────────────────────────────────────────
function PledgeSlice({ pct, label, note, tid }) {
  return (
    <div className="border border-sage-border p-4" data-testid={tid}>
      <div className="text-3xl md:text-4xl font-bold tabular-nums text-sage-accent">{pct}</div>
      <div className="text-xs text-sage-text uppercase tracking-tight font-semibold mt-1">{label}</div>
      <div className="text-[11px] text-sage-muted mt-2 sage-prose">{note}</div>
    </div>
  );
}

function CauseCard({ idx, name, note, link }) {
  const inner = (
    <>
      <div className="text-[10px] tabular-nums text-sage-faint mb-1">{idx}</div>
      <div className="text-xl font-bold uppercase tracking-tight text-sage-text mb-1">{name}</div>
      <div className="text-xs text-sage-muted sage-prose">{note}</div>
    </>
  );
  if (link) {
    return (
      <Link to={link} className="border border-sage-border p-4 hover:border-sage-accent transition-colors block">
        {inner}
        <div className="text-[10px] uppercase tracking-[0.15em] text-sage-accent mt-2">Read the mechanic →</div>
      </Link>
    );
  }
  return <div className="border border-sage-border p-4">{inner}</div>;
}

function FloorItem({ name, note }) {
  return (
    <div className="border border-sage-border p-3 hover:border-sage-accent/60 transition-colors">
      <div className="text-sm font-bold text-sage-text uppercase tracking-tight">{name}</div>
      <div className="text-[11px] text-sage-muted sage-prose mt-1">{note}</div>
    </div>
  );
}

function OffLedgerItem({ name, note }) {
  return (
    <div className="border-l-2 pl-3" style={{ borderColor: "#d97757" }}>
      <div className="text-sm font-bold text-sage-text uppercase tracking-tight">{name}</div>
      <div className="text-[11px] text-sage-muted sage-prose mt-1">{note}</div>
    </div>
  );
}

function LoopCard({ houseName, accent, summary, href, external, isSelf, tid }) {
  const inner = (
    <div
      className={`border-2 p-5 transition-colors ${isSelf ? "" : "hover:opacity-95"}`}
      style={{ borderColor: accent, backgroundColor: `${accent}10` }}
    >
      <div
        className="text-2xl font-bold uppercase tracking-tight mb-2"
        style={{ color: accent }}
        dangerouslySetInnerHTML={{ __html: houseName }}
      />
      <div className="text-xs text-sage-text sage-prose mb-3" dangerouslySetInnerHTML={{ __html: summary }} />
      <div className="text-[10px] uppercase tracking-[0.15em]" style={{ color: accent }}>
        {isSelf ? "You are here" : external ? "Visit house ↗" : "Visit house →"}
      </div>
    </div>
  );
  if (isSelf) {
    return <div data-testid={tid}>{inner}</div>;
  }
  if (external) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" data-testid={tid}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={href} data-testid={tid}>
      {inner}
    </Link>
  );
}
